/**
 * The host half of the IDE's Tests bar: list a project's tests, and run them
 * over the API's SSE run endpoint.
 *
 * `@molecule/app-ide-react` owns no routes, so the bar asks the host for both.
 * This module is that host implementation for molecule.dev — `GET
 * /projects/:id/tests` and `POST /projects/:id/tests/run`, the latter read as a
 * `text/event-stream` exactly like the sandbox-boot stream in `Workspace.tsx`.
 *
 * `watchProjectTestRuns` follows `GET /projects/:id/tests/runs`: every run in the
 * project, whoever started it, so the bar can show the agent's and the platform's
 * runs too.
 *
 * Cancelling POSTs `…/tests/run/:runId/stop` and then aborts the request. Both
 * halves matter: the POST is what actually ends the run, and the abort is what
 * stops this client reading it. Relying on the abort alone was the original
 * design and it did not work — the disconnect never reached the API, so the run
 * ran on to the end of its list while the interface said it had stopped.
 *
 * `skipCurrent()` is the smaller cut: `POST /projects/:id/tests/run/:runId/skip`
 * drops the command the run is on and lets the run carry on to the next one.
 * The files that command owned come back on the SAME stream as `result`s with
 * `status: 'skipped'`, so nothing here has to report anything — the run does.
 *
 * @module
 */

import type {
  TestList,
  TestRunEvent,
  TestRunFeedEvent,
  TestRunHandle,
  TestSelection,
} from '@molecule/app-ide-react'

import { API_BASE_URL } from '../config.js'
import { isSandboxUnavailableBody, sandboxErrorCopy } from '../utilities/sandbox-error-copy.js'

/** The minimum of the bonded HTTP client this module needs. */
export interface TestsHttpClient {
  get<T>(url: string): Promise<{ data: T }>
  getAuthToken(): string | null | undefined
}

/**
 * List a project's tests.
 *
 * @param http - The bonded HTTP client.
 * @param projectId - The project.
 * @returns The discovered tests and the runners available per workspace.
 */
export async function listProjectTests(
  http: TestsHttpClient,
  projectId: string,
): Promise<TestList> {
  const response = await http.get<TestList & { sandbox?: 'unavailable' }>(
    `/projects/${projectId}/tests`,
  )
  // Asleep / booting / lost: an empty list would read as "no tests". Say why
  // there is nothing to show instead.
  if (isSandboxUnavailableBody(response.data)) {
    throw new Error(sandboxErrorCopy('sandbox.error.sleeping') ?? '')
  }
  return { tests: response.data?.tests ?? [], runners: response.data?.runners ?? {} }
}

/** Everything {@link runProjectTests} needs. */
export interface RunProjectTestsOptions {
  /** The versioned panel controls durable native invocations directly. */
  version?: 2
  projectId: string
  selection: TestSelection
  /** The bearer token, when there is one (a guest session rides the cookie). */
  authToken?: string | null
  /** Called for every event the run streams, in order. */
  onEvent: (event: TestRunEvent) => void
  /** Overridable for tests. */
  baseUrl?: string
  /** Overridable for tests. */
  fetchImpl?: typeof fetch
}

/**
 * Start a run and stream its events back.
 *
 * The bar is given a `done` event no matter how the run ends — including when
 * the request never opened, when the server refused it, and when the stream
 * dies mid-run. A bar left spinning forever because nothing answered is the one
 * outcome this must never produce.
 *
 * @param options - See {@link RunProjectTestsOptions}.
 * @returns A handle whose `cancel()` aborts the run.
 */
export function runProjectTests(options: RunProjectTestsOptions): TestRunHandle {
  const { projectId, selection, authToken, onEvent } = options
  const base = options.baseUrl ?? API_BASE_URL
  const doFetch = options.fetchImpl ?? fetch
  const controller = new AbortController()
  let cancelled = false
  let sawDone = false
  // The run's own id, learned from its `start` event — the skip route is keyed
  // by it, and until it arrives there is nothing to skip yet.
  let runId: string | null = null

  const finish = (outcome: 'cancelled' | 'error', error?: string): void => {
    if (sawDone) return
    sawDone = true
    onEvent({ type: 'done', outcome, ...(error ? { error } : {}) })
  }

  void (async () => {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    }
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`

    let response: Response
    try {
      response = await doFetch(
        `${base}/projects/${projectId}/tests/run${options.version === 2 ? '?v=2' : ''}`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(selection),
          signal: controller.signal,
        },
      )
    } catch (error) {
      finish(
        cancelled ? 'cancelled' : 'error',
        cancelled ? undefined : errorMessage(error, 'The test run could not be started.'),
      )
      return
    }

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: unknown }
      finish(
        'error',
        typeof body.error === 'string' && body.error
          ? body.error
          : `The test run could not be started (${response.status}).`,
      )
      return
    }
    if (!response.body) {
      finish('error', 'The test run returned no output.')
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: TestRunEvent
          try {
            event = JSON.parse(line.slice('data: '.length)) as TestRunEvent
          } catch (_error) {
            // A partial frame; the next chunk completes it.
            continue
          }
          if (event.type === 'done') sawDone = true
          if (event.type === 'start') runId = event.runId
          onEvent(event)
        }
      }
    } catch (error) {
      finish(
        cancelled ? 'cancelled' : 'error',
        cancelled ? undefined : errorMessage(error, 'The test run’s connection dropped.'),
      )
      return
    }
    // The stream ended without a `done` — the server went away mid-run.
    finish(cancelled ? 'cancelled' : 'error', cancelled ? undefined : 'The test run was cut short.')
  })()

  const handle: TestRunHandle = {
    cancel(): void {
      cancelled = true
      // TELL the server first, then stop reading. Aborting alone does not stop
      // the run: measured against production on 2026-09-17, the abort never
      // reached Express, so the run carried on to the next spec file — still
      // driving the project's preview — while this client reported it stopped.
      // The request is fire-and-forget because the local stop must not wait on
      // the network, and a 404 just means the run had already finished.
      if (runId) {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`
        void doFetch(`${base}/projects/${projectId}/tests/run/${runId}/stop`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body:
            options.version === 2
              ? JSON.stringify({ planId: runId, requestId: crypto.randomUUID() })
              : '{}',
        }).catch(() => {
          // Nothing to surface: the run is going away from this client's point
          // of view either way, and the server's own disconnect backstop
          // remains. Reporting a failed stop would be noise the person cannot
          // act on.
        })
      }
      controller.abort()
    },
    async skipCurrent(): Promise<boolean> {
      // No `start` yet, or the run is over: there is nothing to skip, which is
      // a race the person did not cause — the card drops the control silently.
      if (!runId || sawDone || cancelled) return false
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`
      try {
        const response = await doFetch(`${base}/projects/${projectId}/tests/run/${runId}/skip`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: '{}',
        })
        // A 404 means the run had already moved on — benign, and the only
        // honest answer is "nothing was skipped", never an error in the card.
        if (!response.ok) return false
        const body = (await response.json().catch(() => ({}))) as { skipped?: unknown }
        return body.skipped !== false
      } catch (_error) {
        // Offline or aborted mid-request: nothing was skipped, and the run's
        // own stream is what reports anything that actually happened.
        return false
      }
    },
  }
  return options.version === 2 ? { cancel: handle.cancel } : handle
}

/** Everything {@link watchProjectTestRuns} needs. */
export interface WatchProjectTestRunsOptions {
  projectId: string
  /** The CURRENT bearer token, read again on every reconnect. */
  getAuthToken?: () => string | null | undefined
  /** Called for every event of every run in the project, in order. */
  onEvent: (event: TestRunFeedEvent) => void
  /** Overridable for tests. */
  baseUrl?: string
  /** Overridable for tests. */
  fetchImpl?: typeof fetch
  /** Overridable for tests: how long to wait before reconnect attempt `n` (from 0). */
  retryDelayMs?: (attempt: number) => number
}

/** Reconnect backoff: 1 s, doubling, capped at 30 s. */
const defaultRetryDelayMs = (attempt: number): number => Math.min(30_000, 1000 * 2 ** attempt)

/**
 * Follow every test run in the project — the Run buttons in any tab, the
 * agent's test tool and its own shell test commands, the platform's checks —
 * over `GET /projects/:id/tests/runs`. The API replays the runs it still holds
 * on connect, then streams new events.
 *
 * The connection is held for as long as the IDE is open, so a dropped one is
 * reopened (with backoff). Every event carries its Redis Stream id; the next
 * connection sends the last applied id in `Last-Event-ID`, and the server
 * resumes strictly after it. The card therefore keeps its state across a quiet
 * reconnect instead of receiving synthetic failures or a reordered history. A
 * refusal (no access, no such project) ends the watch for good.
 *
 * @param options - See {@link WatchProjectTestRunsOptions}.
 * @returns The unsubscribe function.
 */
export function watchProjectTestRuns(options: WatchProjectTestRunsOptions): () => void {
  const { projectId, onEvent } = options
  const base = options.baseUrl ?? API_BASE_URL
  const doFetch = options.fetchImpl ?? fetch
  const retryDelayMs = options.retryDelayMs ?? defaultRetryDelayMs
  const controller = new AbortController()
  let stopped = false
  let lastEventId: string | null = null

  /** One connection: how it ended. */
  const follow = async (): Promise<'refused' | 'dropped' | 'streamed'> => {
    const headers: Record<string, string> = { Accept: 'text/event-stream' }
    if (lastEventId) headers['Last-Event-ID'] = lastEventId
    const authToken = options.getAuthToken?.()
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`
    let response: Response
    try {
      response = await doFetch(`${base}/projects/${projectId}/tests/runs`, {
        method: 'GET',
        headers,
        credentials: 'include',
        signal: controller.signal,
      })
    } catch (_error) {
      // Offline, or unsubscribed mid-request: the loop decides which.
      return 'dropped'
    }
    if (response.status >= 400 && response.status < 500) return 'refused'
    if (!response.ok || !response.body) return 'dropped'
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let streamed = false
    try {
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          let event: TestRunFeedEvent
          try {
            event = JSON.parse(line.slice('data: '.length)) as TestRunFeedEvent
          } catch (_error) {
            // A partial frame; the next chunk completes it.
            continue
          }
          streamed = true
          if (event.eventId) lastEventId = event.eventId
          onEvent(event)
        }
      }
    } catch (_error) {
      // The connection dropped, or the watch was stopped; handled below.
    }
    return streamed ? 'streamed' : 'dropped'
  }

  void (async () => {
    for (let attempt = 0; !stopped;) {
      const ended = await follow()
      if (stopped || ended === 'refused') return
      // A connection that delivered anything was healthy: start the backoff over.
      attempt = ended === 'streamed' ? 0 : attempt + 1
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, retryDelayMs(attempt))
        controller.signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer)
            resolve()
          },
          { once: true },
        )
      })
    }
  })()

  return () => {
    stopped = true
    controller.abort()
  }
}

/**
 * A readable message for a thrown value.
 *
 * @param error - What was thrown.
 * @param fallback - What to say when it carries no message.
 * @returns The message to show.
 */
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}
